// User Avatar Service - Ready for API integration
export interface UserAvatarData {
    userId: string;
    avatarUrl: string;
    fallbackColor?: string;
    displayName: string;
}

class UserAvatarService {
    // Static mapping for now - can be replaced with API calls
    private static avatarMap: { [key: string]: UserAvatarData } = {
        'elizabeth-fisher': {
            userId: 'elizabeth-fisher',
            avatarUrl: 'https://storage.googleapis.com/heysynth_files/synth_webapp/message-icon-1.svg',
            displayName: 'Elizabeth Fisher',
            fallbackColor: '#6366f1'
        },
        'matt-mahoney': {
            userId: 'matt-mahoney', 
            avatarUrl: 'https://storage.googleapis.com/heysynth_files/synth_webapp/message-icon-2.svg',
            displayName: 'Matt Mahoney',
            fallbackColor: '#8b5cf6'
        }
    };

    /**
     * Get user avatar data by user ID
     * In the future, this method can make an API call to fetch user data
     */
    static async getUserAvatar(userId: string): Promise<UserAvatarData | null> {
        // TODO: Replace with actual API call
        // const response = await fetch(`/api/users/${userId}/avatar`);
        // return response.json();
        
        return this.avatarMap[userId] || null;
    }

    /**
     * Get user avatar data synchronously (for current static implementation)
     */
    static getUserAvatarSync(userId: string): UserAvatarData | null {
        return this.avatarMap[userId] || null;
    }

    /**
     * Add or update user avatar data
     * Useful for caching API responses
     */
    static setUserAvatar(userId: string, avatarData: UserAvatarData): void {
        this.avatarMap[userId] = avatarData;
    }

    /**
     * Batch load multiple user avatars
     * Ready for API optimization with batch requests
     */
    static async getUserAvatars(userIds: string[]): Promise<{ [key: string]: UserAvatarData }> {
        // TODO: Replace with batch API call
        // const response = await fetch('/api/users/avatars', {
        //     method: 'POST',
        //     body: JSON.stringify({ userIds }),
        //     headers: { 'Content-Type': 'application/json' }
        // });
        // return response.json();

        const result: { [key: string]: UserAvatarData } = {};
        userIds.forEach(userId => {
            const avatar = this.getUserAvatarSync(userId);
            if (avatar) {
                result[userId] = avatar;
            }
        });
        return result;
    }

    /**
     * Generate initials fallback for users without avatars
     */
    static generateInitials(displayName: string): string {
        // Safety check for undefined or null displayName
        if (!displayName || typeof displayName !== 'string') {
            console.warn('generateInitials: displayName is undefined or not a string', displayName);
            return 'UN'; // Return 'Unknown' initials as fallback
        }
        
        return displayName
            .split(' ')
            .map(word => word.charAt(0).toUpperCase())
            .slice(0, 2)
            .join('');
    }

    /**
     * Get a fallback color based on user ID (deterministic)
     */
    static getFallbackColor(userId: string): string {
        const colors = [
            '#ef4444', '#f97316', '#eab308', '#22c55e',
            '#06b6d4', '#3b82f6', '#8b5cf6', '#ec4899'
        ];
        
        // Safety check for undefined or null userId
        if (!userId || typeof userId !== 'string') {
            console.warn('getFallbackColor: userId is undefined or not a string', userId);
            return colors[0]; // Return first color as fallback
        }
        
        // Generate deterministic color based on user ID
        let hash = 0;
        for (let i = 0; i < userId.length; i++) {
            hash = userId.charCodeAt(i) + ((hash << 5) - hash);
        }
        return colors[Math.abs(hash) % colors.length];
    }
}

export default UserAvatarService;
